// src/utils/chartAccess.ts
//
// FS-548: device access for the in-app chart flag. Turning the flag on for an
// app user is only half the job — the chart endpoint also requires that user's
// devices to be listed in a separate, server-only record. Without it the app
// shows a chart icon that never opens. firebase-api proposes that record from
// the devices each user has registered in the app and cross-checks every one
// against the fleet records; an admin reviews the proposal before it is saved.

/** The one flag that carries a device-access record. */
export const CHART_FLAG_NAME = 'in_app_charts';

export interface PlanDevice {
  serial: string;
  device_id: number | null;
  description: string | null;
  /** False when the device is not in the fleet records at all. */
  known_device: boolean;
  /** Every app user who has this device registered — more than one is a conflict. */
  claimed_by: string[];
  change: 'grant' | 'unchanged';
}

export interface PlanUser {
  uid: string;
  email: string | null;
  name: string | null;
  user_exists: boolean;
  /** Devices this user can currently open charts for. */
  current: string[];
  proposed: string[];
  devices: PlanDevice[];
  /** Currently allowed but no longer registered to this user. */
  revoked: string[];
  change: 'none' | 'create' | 'update' | 'clear';
  warnings: string[];
}

export interface StaleMapping {
  uid: string;
  email: string | null;
  devices: string[];
}

export interface ChartAccessPlan {
  flag: string;
  generated_at: string;
  users: PlanUser[];
  /** Records for users no longer on the flag — applying removes them. */
  stale_mappings: StaleMapping[];
  summary: {
    users: number;
    grants: number;
    revocations: number;
    users_without_devices: number;
    warnings: number;
  };
  access: Record<string, string[]>;
  /**
   * The saved device access this proposal was computed against. Sent back when
   * saving so the server can refuse a proposal that another admin has already
   * overtaken, instead of silently undoing their change.
   */
  baseline: Record<string, string[]>;
}

export interface ApplyReceipt {
  applied_at?: string;
  written?: Array<{ uid: string; devices: string[] }>;
  removed?: Array<{ uid: string; devices: string[] }>;
  unchanged?: string[];
  users_written?: number;
  users_removed?: number;
}

/** Identifies one proposed device row across users; serials are case-blind. */
export const deviceKey = (uid: string, serial: string): string => `${uid}::${serial.toLowerCase()}`;

/** Who the row is about, in the order an admin recognizes. */
export const userLabel = (user: { name: string | null; email: string | null; uid: string }): string =>
  user.email || user.name || user.uid;

export const warningText: Record<string, string> = {
  no_devices:
    'Turned on, but no devices to show — charts will not open for this user until a device is registered to them.',
  unknown_device: 'A device registered to this user is not in the fleet records.',
  shared_claim: 'A device here is registered to more than one app user.',
  no_user_doc: 'No app user record found for this ID.',
};

/** The reviewed selection, in the shape the save endpoint expects. */
export const buildAccessMap = (
  plan: ChartAccessPlan,
  excluded: Set<string>,
): Record<string, string[]> =>
  Object.fromEntries(
    plan.users.map((user) => [
      user.uid,
      user.devices
        .filter((device) => !excluded.has(deviceKey(user.uid, device.serial)))
        .map((device) => device.serial),
    ]),
  );

/**
 * What saving would actually do, given what the admin has unticked. Unticking
 * a device that is already allowed is a removal, which is why this is counted
 * from the selection rather than read off the server's proposal.
 */
export const selectionSummary = (
  plan: ChartAccessPlan,
  excluded: Set<string>,
): { grants: number; revocations: number; changes: number } => {
  let grants = 0;
  let revocations = 0;
  for (const user of plan.users) {
    const selected = user.devices
      .filter((device) => !excluded.has(deviceKey(user.uid, device.serial)))
      .map((device) => device.serial.toLowerCase());
    const current = user.current.map((serial) => serial.toLowerCase());
    grants += selected.filter((serial) => !current.includes(serial)).length;
    revocations += current.filter((serial) => !selected.includes(serial)).length;
  }
  for (const stale of plan.stale_mappings) revocations += stale.devices.length;
  return { grants, revocations, changes: grants + revocations };
};

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

/** Plain receipt for the page banner after a save. */
export const receiptText = (receipt: ApplyReceipt): string => {
  const written = receipt.users_written ?? receipt.written?.length ?? 0;
  const removed = receipt.users_removed ?? receipt.removed?.length ?? 0;
  if (written === 0 && removed === 0) return 'Device access was already up to date — nothing changed.';
  const parts: string[] = [];
  if (written > 0) parts.push(`updated ${plural(written, 'user')}`);
  if (removed > 0) parts.push(`removed access for ${plural(removed, 'user')}`);
  return `Device access saved — ${parts.join(', ')}.`;
};
